import "./SideDrawer.css";
import React, { useId } from "react";
import { createPortal } from "react-dom";
import IconButton from "../button/IconButton";
import classNames from "classnames";

export type SideDrawerSide = "left" | "right";

export interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  side: SideDrawerSide;
  closeOnOutsideClick: boolean;
  showCloseButton?: boolean;
  children: React.ReactNode;
  noPadding: boolean;
}

const SideDrawer: React.FC<SideDrawerProps> = ({
  isOpen,
  onClose,
  title,
  side,
  closeOnOutsideClick,
  showCloseButton = false,
  noPadding = false,
  children,
}) => {
  const titleId = useId();
  const placementClass =
    side === "left" ? "offcanvas-start" : "offcanvas-end";

  const closeButton = showCloseButton ? (
    <IconButton
      onClick={onClose}
      aria-label="Close drawer"
      iconClassName="bi bi-x-circle"
    />
  ) : null;

  return createPortal(
    <>
      <aside
        className={classNames("offcanvas side-drawer kg-bg-white", placementClass, {
          "showing": isOpen
        })}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
        aria-labelledby={titleId}
      >
        <div
          className={classNames(
            "offcanvas-header border-bottom kg-border-gray",
            side === "right" ? "justify-content-start" : "justify-content-between"
          )}
        >
          {side === "right" && closeButton}
          <h5
            id={titleId}
            className={`offcanvas-title kg-dark${
              showCloseButton
                ? side === "right"
                  ? " ms-3"
                  : " me-3"
                : ""
            }`}
          >
            {title}
          </h5>
          {side === "left" && closeButton}
        </div>

        <div className={classNames("offcanvas-body kg-light-dark", {"p-0": noPadding})}>{children}</div>
      </aside>

      {isOpen && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={closeOnOutsideClick ? onClose : undefined}
          aria-hidden="true"
        />
      )}
    </>,
    document.body
  );
};

export default SideDrawer;
